/*
|--------------------------------------------------------------------------
| Shared API error model
|--------------------------------------------------------------------------
|
| User-facing messages stay safe and Persian. Technical context remains on
| the error object for Console logging and future monitoring integrations.
|
*/
export class ApiError extends Error {
  constructor({
    message,
    kind = "unknown",
    status = 0,
    operation = "unknown",
    retryable = false,
    technicalMessage = "",
    cause,
  }) {
    super(message);

    this.name = "ApiError";
    this.kind = kind;
    this.status = status;
    this.operation = operation;
    this.retryable = retryable;
    this.technicalMessage = technicalMessage;

    if (cause !== undefined) {
      this.cause = cause;
    }
  }
}

export const isAbortError = (error) => {
  return error?.name === "AbortError";
};

const isBrowserOffline = () => {
  return (
    typeof navigator !== "undefined" &&
    navigator.onLine === false
  );
};

const getHttpErrorMessage = ({
  status,
  notFoundMessage,
  defaultMessage,
}) => {
  if (status === 404 && notFoundMessage) {
    return notFoundMessage;
  }

  if (status === 408 || status === 504) {
    return "پاسخ سرور بیشتر از حد معمول طول کشید";
  }

  if (status === 429) {
    return "تعداد درخواست‌ها زیاد است؛ کمی بعد دوباره بررسی می‌شود";
  }

  if (status >= 500) {
    return "سرور موقتاً پاسخ نمی‌دهد";
  }

  if (status >= 400) {
    return defaultMessage || "درخواست قابل انجام نیست";
  }

  return defaultMessage || "دریافت اطلاعات با مشکل مواجه شد";
};

const request = async ({
  url,
  operation,
  signal,
  timeoutMs = 10000,
  notFoundMessage = "",
  defaultMessage = "دریافت اطلاعات با مشکل مواجه شد",
  parseJson = true,
  ...fetchOptions
}) => {
  const controller = new AbortController();
  let didTimeout = false;
  let timeoutId = null;

  const abortFromCaller = () => {
    controller.abort();
  };

  if (signal) {
    if (signal.aborted) {
      controller.abort();
    } else {
      signal.addEventListener("abort", abortFromCaller, { once: true });
    }
  }

  if (timeoutMs > 0) {
    timeoutId = window.setTimeout(() => {
      didTimeout = true;
      controller.abort();
    }, timeoutMs);
  }

  try {
    const response = await fetch(url, {
      ...fetchOptions,
      signal: controller.signal,
    });

    if (!response.ok) {
      const message = getHttpErrorMessage({
        status: response.status,
        notFoundMessage,
        defaultMessage,
      });

      throw new ApiError({
        message,
        kind: response.status === 404 ? "not-found" : "http",
        status: response.status,
        operation,
        retryable: response.status === 429 || response.status >= 500,
        technicalMessage: `HTTP ${response.status} ${response.statusText}`,
      });
    }

    if (!parseJson) {
      return response;
    }

    try {
      return await response.json();
    } catch (error) {
      throw new ApiError({
        message: "پاسخ دریافتی از سرور معتبر نیست",
        kind: "parse",
        status: response.status,
        operation,
        retryable: true,
        technicalMessage: error?.message || "Invalid JSON response",
        cause: error,
      });
    }
  } catch (error) {
    if (error instanceof ApiError) {
      throw error;
    }

    if (signal?.aborted) {
      throw error;
    }

    if (didTimeout) {
      throw new ApiError({
        message: "پاسخ سرور بیشتر از حد معمول طول کشید",
        kind: "timeout",
        operation,
        retryable: true,
        technicalMessage: `Request timed out after ${timeoutMs}ms`,
        cause: error,
      });
    }

    if (isAbortError(error)) {
      throw error;
    }

    if (isBrowserOffline()) {
      throw new ApiError({
        message: "اتصال اینترنت خود را بررسی کنید",
        kind: "offline",
        operation,
        retryable: true,
        technicalMessage: error?.message || "Browser is offline",
        cause: error,
      });
    }

    throw new ApiError({
      message: "ارتباط با سرور برقرار نشد",
      kind: "network",
      operation,
      retryable: true,
      technicalMessage: error?.message || "Network request failed",
      cause: error,
    });
  } finally {
    if (timeoutId !== null) {
      window.clearTimeout(timeoutId);
    }

    signal?.removeEventListener?.("abort", abortFromCaller);
  }
};

export const requestJson = (options) => {
  return request({
    ...options,
    parseJson: true,
  });
};

export const requestWithoutBody = (options) => {
  return request({
    ...options,
    parseJson: false,
  });
};
